
import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { MessageCircle, Send, Sparkles, BarChart3, Database, Brain, TrendingUp } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import ChatMessage from './ChatMessage';
import ConversationSidebar from './ConversationSidebar';
import { 
  useGetChippieConversations, 
  useGetChippieMessages, 
  useCreateChippieConversation, 
  useCreateChippieMessage 
} from '@/hooks/use-chippie-conversations';
import { ChippieConversation, ChippieMessage } from '@/types/database.types';

const ChippieChatInterface: React.FC = () => {
  const { user } = useAuth();
  const [currentConversation, setCurrentConversation] = useState<ChippieConversation | null>(null);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Hooks for data management
  const { data: conversations = [], isLoading: conversationsLoading } = useGetChippieConversations();
  const { data: messages = [] } = useGetChippieMessages(currentConversation?.id || null);
  const createConversationMutation = useCreateChippieConversation();
  const createMessageMutation = useCreateChippieMessage();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const createNewConversation = async () => {
    try {
      // Clear current conversation state immediately
      setCurrentConversation(null);
      setInputMessage('');
      
      const newConversation = await createConversationMutation.mutateAsync({
        title: 'New Conversation',
      });
      setCurrentConversation(newConversation);
      toast.success('New conversation started');
    } catch (error) {
      console.error('Error creating conversation:', error);
      toast.error('Failed to create new conversation');
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || !user) return;

    let conversation = currentConversation;
    
    // Create new conversation if none exists
    if (!conversation) {
      try {
        conversation = await createConversationMutation.mutateAsync({
          title: inputMessage.slice(0, 50) + (inputMessage.length > 50 ? '...' : ''),
        });
        setCurrentConversation(conversation);
      } catch (error) {
        console.error('Error creating conversation:', error);
        toast.error('Failed to create conversation');
        return;
      }
    }

    // Save user message to database
    try {
      await createMessageMutation.mutateAsync({
        conversation_id: conversation.id,
        role: 'user',
        content: inputMessage,
      });
    } catch (error) {
      console.error('Error saving user message:', error);
      toast.error('Failed to save message');
      return;
    }

    const userMessageContent = inputMessage;
    setInputMessage('');
    setIsLoading(true);

    try {
      console.log('Sending message to Chippie AI:', userMessageContent);
      
      // Call the new RAG-based chippie-ai edge function with context
      let data, error;
      
      try {
        console.log('Attempting RAG-based AI...');
        const ragResponse = await supabase.functions.invoke('chippie-ai-rag', {
          body: {
            message: userMessageContent,
            conversationHistory: messages.length > 0 ? messages.slice(-10).map(msg => ({
              role: msg.role,
              content: msg.content
            })) : [],
            userId: user.id
          }
        });
        
        data = ragResponse.data;
        error = ragResponse.error;
        
        if (error) {
          console.log('RAG failed, falling back to enhanced AI...');
          const fallbackResponse = await supabase.functions.invoke('chippie-ai-enhanced', {
            body: {
              message: userMessageContent,
              conversation_id: conversation.id,
              user_id: user.id,
              conversation_history: messages.length > 0 ? messages.slice(-10).map(msg => ({
                role: msg.role,
                content: msg.content
              })) : []
            }
          });
          
          data = fallbackResponse.data;
          error = fallbackResponse.error;
          
          // Mark that fallback was used
          if (data) {
            data.metadata = { ...data.metadata, fallback_used: true };
          }
        } else {
          // Mark that RAG was successfully used
          if (data) {
            data.metadata = { ...data.metadata, rag_used: true };
          }
        }
      } catch (ragError) {
        console.error('RAG system error, using fallback:', ragError);
        const fallbackResponse = await supabase.functions.invoke('chippie-ai-enhanced', {
          body: {
            message: userMessageContent,
            conversation_id: conversation.id,
            user_id: user.id,
            conversation_history: messages.length > 0 ? messages.slice(-10).map(msg => ({
              role: msg.role,
              content: msg.content
            })) : []
          }
        });
        
        data = fallbackResponse.data;
        error = fallbackResponse.error;
        
        if (data) {
          data.metadata = { ...data.metadata, fallback_used: true, rag_error: ragError.message };
        }
      }

      if (error) {
        throw error;
      }

      // Check if the response contains an error (even with status 200)
      if (data?.success === false || data?.error) {
        throw new Error(data?.response || data?.error || 'Unknown error occurred');
      }

      console.log('Chippie AI response:', data);

      // Save assistant message to database
      await createMessageMutation.mutateAsync({
        conversation_id: conversation.id,
        role: 'assistant',
        content: data.response || 'I apologize, but I encountered an issue processing your request.',
        metadata: {
          intent: data.intent,
          response_type: data.response_type,
          data: data.data,
          insights: data.insights,
          metadata: data.metadata
        }
      });

    } catch (error) {
      console.error('Error calling Chippie AI:', error);
      
      // Extract more specific error information
      let errorMessage = 'I apologize, but I encountered an error while processing your request.';
      
      if (error?.message) {
        errorMessage += ` Error: ${error.message}`;
      }
      
      // If there's response data with error details, use that
      if (error?.context?.body) {
        try {
          const errorData = JSON.parse(error.context.body);
          if (errorData.error) {
            errorMessage = `I encountered an error: ${errorData.error}`;
            if (errorData.debug) {
              console.log('Debug information:', errorData.debug);
              errorMessage += ' Please check if all required API keys are configured.';
            }
          }
        } catch (parseError) {
          console.error('Failed to parse error response:', parseError);
        }
      }
      
      // Save error message to database
      try {
        await createMessageMutation.mutateAsync({
          conversation_id: conversation.id,
          role: 'assistant',
          content: errorMessage,
        });
      } catch (saveError) {
        console.error('Error saving error message:', saveError);
        toast.error('Failed to save error message');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const quickActions = [
    {
      icon: BarChart3,
      label: "Show Procurement Analytics",
      query: "Show me a summary of all procurements with their current status and total amounts"
    },
    {
      icon: TrendingUp,
      label: "Price Trends",
      query: "What are the current price trends for our main products?"
    },
    {
      icon: Database,
      label: "Supplier Performance",
      query: "Which suppliers have the best performance based on delivery times and quality?"
    },
    {
      icon: Brain,
      label: "Supply Chain Insights",
      query: "Give me insights about our supply chain efficiency and recommendations for improvement"
    }
  ];

  const handleQuickAction = async (query: string) => {
    if (!currentConversation) {
      await createNewConversation();
    }
    setInputMessage(query);
  };

  return (
    <div className="flex h-full w-full overflow-hidden">
      {/* Conversation Sidebar */}
      <ConversationSidebar
        conversations={conversations}
        currentConversation={currentConversation}
        onSelectConversation={setCurrentConversation}
        onNewConversation={createNewConversation}
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        isLoading={conversationsLoading}
      />

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Chat Header */}
        <Card className="rounded-none border-l-0 border-r-0 border-t-0 flex-shrink-0">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2">
              <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg">
                <Sparkles className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Chippie AI RAG</h1>
                <p className="text-sm text-muted-foreground font-normal">
                  Retrieval Augmented Generation • Semantic Search • Vector Database Intelligence
                </p>
                {currentConversation?.conversation_summary && (
                  <div className="mt-2 p-2 bg-muted/50 rounded-md">
                    <p className="text-xs text-muted-foreground">Context: {currentConversation.conversation_summary}</p>
                  </div>
                )}
              </div>
            </CardTitle>
          </CardHeader>
        </Card>

        {/* Messages Area */}
        <div className="flex-1 flex flex-col min-h-0">
          {currentConversation ? (
            <ScrollArea className="flex-1 p-4">
              <div className="space-y-4 max-w-4xl mx-auto">
                {messages.map((message) => (
                  <ChatMessage key={message.id} message={message} />
                ))}
                {isLoading && (
                  <ChatMessage
                    message={{
                      id: 'loading',
                      conversation_id: currentConversation.id,
                      role: 'assistant',
                      content: '...',
                      created_at: new Date().toISOString(),
                    }}
                    isLoading={true}
                  />
                )}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>
          ) : (
            <div className="flex-1 flex items-center justify-center p-4">
              <div className="text-center space-y-6 max-w-2xl">
                <div className="p-4 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full w-16 h-16 mx-auto flex items-center justify-center">
                  <MessageCircle className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold mb-2">Welcome to Chippie AI</h2>
                  <p className="text-muted-foreground">
                    Your intelligent supply chain assistant powered by advanced AI. I can analyze your data,
                    provide insights, generate reports, and answer questions about your entire supply chain operation.
                  </p>
                </div>
                
                <div className="space-y-3">
                  <p className="text-sm font-medium text-muted-foreground">Try these quick actions:</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {quickActions.map((action, index) => (
                      <Button
                        key={index}
                        variant="outline"
                        className="gap-2 h-auto p-3 text-left justify-start"
                        onClick={() => handleQuickAction(action.query)}
                      >
                        <action.icon className="h-4 w-4 flex-shrink-0" />
                        <span className="text-sm">{action.label}</span>
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Input Area */}
          <div className="border-t p-4 flex-shrink-0">
            <div className="max-w-4xl mx-auto">
              <div className="flex gap-2">
                <Input
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask me anything about your supply chain data..."
                  className="flex-1"
                  disabled={isLoading || !user}
                />
                <Button 
                  onClick={handleSendMessage} 
                  disabled={!inputMessage.trim() || isLoading || !user}
                  className="gap-2"
                >
                  <Send className="h-4 w-4" />
                  Send
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChippieChatInterface;
